import {Box, Button, createStyles, Group, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import ProteinCreationSpreadsheetImportModal from 'antibodies/nested-data-creation/full-screen/ProteinCreationSpreadsheetImportModal';
import {ProteinCreationStructuredTable} from 'antibodies/nested-data-creation/full-screen/ProteinCreationStructuredTableAdapter';
import {
  NdcSpreadsheetComponentFormatType,
  NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import Antibody from 'antibodies/visualization/Antibody';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const TABLE_CID = 'protein-creation-table';
const GLYPH_SIZE = 48;

const useStyles = createStyles((theme) => ({
  headerContainer: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderTopLeftRadius: theme.radius.md,
    borderTopRightRadius: theme.radius.md,
    backgroundColor: theme.white,
  },
  glyphWrapper: {
    width: GLYPH_SIZE,
    height: GLYPH_SIZE,
    flexShrink: 0,
  },
}));

function TableHeader({
  format,
  onAddFromSpreadsheet,
  startingType,
}: {
  format: AntibodyFormat;
  onAddFromSpreadsheet: () => void;
  startingType: NdcSpreadsheetComponentFormatType;
}): JSX.Element {
  const {classes} = useStyles();
  const startingTypeLabel = NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[startingType];

  return (
    <Group
      position="apart"
      noWrap={true}
      px="lg"
      py="sm"
      mx="10px"
      mb="-12px"
      className={classes.headerContainer}
    >
      <Group spacing="md" noWrap={true}>
        <Box className={classes.glyphWrapper}>
          <Antibody format={format} />
        </Box>
        <Stack spacing={2}>
          <Text size="md" weight={600}>
            {format.name} {startingTypeLabel}
          </Text>
          <Text size="xs" c="dimmed" lh="16px">
            Fill each column with an existing Benchling entity or a new DNA or AA sequence. Type or paste
            directly into cells, or pull sequences in from search
          </Text>
        </Stack>
      </Group>
      <Button variant="secondary" leftIcon={<BenchlingIcon icon="upload" />} onClick={onAddFromSpreadsheet}>
        Add from spreadsheet
      </Button>
    </Group>
  );
}

export interface ProteinCreationTableProps {
  format: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
  structuredTable: ProteinCreationStructuredTable;
  handleTableUpdate: (newTable: UnknownStructuredTable) => void;
  onComputedTableChanged?: (computedTable: Array<Array<EvaluatedCell>>) => void;
}

export default function ProteinCreationTable({
  format,
  startingType,
  structuredTable,
  handleTableUpdate,
  onComputedTableChanged,
}: ProteinCreationTableProps): JSX.Element {
  const [isSpreadsheetImportOpen, setIsSpreadsheetImportOpen] = useState(false);

  return (
    <Stack spacing={0}>
      <TableHeader
        format={format}
        onAddFromSpreadsheet={() => {
          setIsSpreadsheetImportOpen(true);
        }}
        startingType={startingType}
      />
      <StructuredTableOutsideNotebook
        structuredTable={structuredTable}
        cid={TABLE_CID}
        updateHandler={handleTableUpdate}
        onComputedTableChanged={onComputedTableChanged}
        enableColumnResizing={true}
        includeUnsubmittedStatus={false}
        hideColumnHeaderIcons={true}
        canRename={false}
        suppressTitleBar={true}
      />
      {isSpreadsheetImportOpen && (
        <ProteinCreationSpreadsheetImportModal
          format={format}
          onClose={() => {
            setIsSpreadsheetImportOpen(false);
          }}
          startingType={startingType}
        />
      )}
    </Stack>
  );
}
